import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type BackupStateResult,
  type PreviewRestoreResult,
} from '@whinerp/client';
import { Button, Num, Panel, SectionLabel, Skeleton, ErrorState } from '@whinerp/ui';
import { formatDateTime } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * Backups, and putting something back (F-145).
 *
 * The specification's own warning is the design: "an unclear backup state
 * causes more anxiety than no backup page at all". So the first thing on the
 * panel is one of two definite sentences - when the last good backup finished,
 * or that there has never been one - and a failure is shown BESIDE that rather
 * than instead of it. A workspace whose backup failed this morning and
 * succeeded last night needs both facts; either one alone is misleading.
 *
 * The restore is previewed and then committed, and the preview is drawn with
 * the scope it was counted under beside every number.
 */
export function BackupPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<'record' | 'object' | 'workspace'>('object');
  const [table, setTable] = useState('contact');
  const [recordId, setRecordId] = useState('');
  const [preview, setPreview] = useState<PreviewRestoreResult | null>(null);

  const state = useQuery<BackupStateResult, ApiError>({
    queryKey: ['backups'],
    queryFn: () => api.backupState({}),
  });

  const refresh = (): Promise<unknown> => queryClient.invalidateQueries({ queryKey: ['backups'] });

  const take = useMutation<unknown, ApiError>({
    mutationFn: () => api.runBackup({}),
    onSuccess: () => refresh(),
  });

  const retention = useMutation<unknown, ApiError, number>({
    mutationFn: (days) => api.setBackupRetention({ body: { days } }),
    onSuccess: () => refresh(),
  });

  const check = useMutation<unknown, ApiError, string>({
    mutationFn: (backupId) => api.verifyBackup({ path: { backupId } }),
    onSuccess: () => refresh(),
  });

  const plan = useMutation<PreviewRestoreResult, ApiError, string>({
    mutationFn: (backupId) =>
      api.previewRestore({
        path: { backupId },
        body: {
          scope,
          ...(scope === 'workspace' ? {} : { objectKey: table }),
          ...(scope === 'record' ? { recordId } : {}),
        },
      }),
    onSuccess: (result) => setPreview(result),
  });

  const commit = useMutation<unknown, ApiError, string>({
    mutationFn: (restoreId) => api.commitRestore({ path: { restoreId } }),
    onSuccess: async () => {
      setPreview(null);
      await refresh();
    },
  });

  if (state.isPending) return <Skeleton className="h-block-lg w-full" />;
  /*
   * A failure is not a loading state.
   *
   * These returned a Skeleton, so a panel whose request failed showed a
   * shimmer for ever - a screen that never loads rather than one that says
   * what went wrong, and a `settled()` that never settles (D-365).
   */
  if (state.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={state.error.message}
        onRetry={() => void state.refetch()}
      />
    );
  }

  const data = state.data;
  const failure = take.error ?? retention.error ?? plan.error ?? commit.error ?? check.error;
  const when = (value: string): string => formatDateTime(value, format);

  return (
    <Panel data-region="backups" id="backups">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('backup.title')}</SectionLabel>

          {/*
            * The one sentence this panel exists for, and it is never absent.
            * "No backup has ever been taken" is the answer a workspace needs
            * most, and the one a page that only lists backups never gives.
            */}
          {data.lastSucceeded ? (
            <p data-backup-last className="text-body text-ink">
              {t('backup.last', {
                values: { when: when(data.lastSucceeded.finishedAt ?? data.lastSucceeded.startedAt) },
              })}
            </p>
          ) : (
            <p data-backup-never className="text-body text-danger-text">
              {t('backup.never')}
            </p>
          )}

          {/* Beside, not instead of. Both facts or neither is useful. */}
          {data.lastFailed ? (
            <p data-backup-failed className="text-dense text-danger-text">
              {t('backup.failed', {
                values: { when: when(data.lastFailed.startedAt), why: data.lastFailed.failure ?? '' },
              })}
            </p>
          ) : null}

          <p className="text-meta text-ink-meta">
            {t('backup.storedIn', { values: { where: data.storedIn } })}
          </p>
        </div>

        {failure ? (
          <p
            data-backup-refusal
            className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text"
          >
            {failure.message}
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-2">
          <Button intent="secondary" size="small" data-backup-run onClick={() => take.mutate()}>
            {t('backup.run')}
          </Button>
          <label className="flex items-center gap-2 text-dense text-ink-600">
            {t('backup.retention')}
            <input
              data-backup-retention
              inputMode="numeric"
              className="w-col-count rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              defaultValue={String(data.retentionDays)}
              onBlur={(event) => {
                const days = Number(event.target.value);
                if (days === data.retentionDays) return;
                retention.mutate(days);
              }}
            />
            {t('backup.days')}
          </label>
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-micro uppercase tracking-label text-ink-meta">{t('backup.list')}</p>
          {data.backups.length === 0 ? (
            <p data-backup-empty className="text-meta text-ink-meta">
              {t('backup.none')}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {data.backups.map((entry) => (
                <li
                  key={entry.backupId}
                  data-backup={entry.backupId}
                  data-backup-status={entry.status}
                  className="flex flex-wrap items-center gap-2 border-t border-t-rule pt-1 first:border-t-0 first:pt-0"
                >
                  <span className="text-dense text-ink">{when(entry.startedAt)}</span>
                  <span className="text-meta text-ink-meta">
                    <Num>{String(entry.rows)}</Num> {t('backup.rows')} · {entry.startedBy}
                  </span>
                  {/* A record that points at nothing is a lie, and the list
                      says so rather than offering a restore that cannot run. */}
                  {entry.present ? null : (
                    <span data-backup-gone className="text-meta text-danger-text">
                      {t('backup.gone')}
                    </span>
                  )}
                  {entry.verifyFailure ? (
                    <span data-backup-unreadable className="text-meta text-danger-text">
                      {entry.verifyFailure}
                    </span>
                  ) : null}
                  {entry.status === 'succeeded' && entry.present ? (
                    <>
                      <Button
                        intent="ghost"
                        size="small"
                        data-backup-verify={entry.backupId}
                        onClick={() => check.mutate(entry.backupId)}
                      >
                        {t('backup.verify')}
                      </Button>
                      <Button
                        intent="ghost"
                        size="small"
                        data-backup-restore={entry.backupId}
                        onClick={() => plan.mutate(entry.backupId)}
                      >
                        {t('backup.restore')}
                      </Button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('backup.scope')}
            <select
              data-restore-scope
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={scope}
              onChange={(event) => setScope(event.target.value as 'record' | 'object' | 'workspace')}
            >
              <option value="record">{t('backup.scope.record')}</option>
              <option value="object">{t('backup.scope.object')}</option>
              <option value="workspace">{t('backup.scope.workspace')}</option>
            </select>
          </label>
          {scope === 'workspace' ? null : (
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('backup.table')}
              <select
                data-restore-table
                className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={table}
                onChange={(event) => setTable(event.target.value)}
              >
                {data.tables.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {scope === 'record' ? (
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('backup.record')}
              <input
                data-restore-record
                className="w-panel-narrow rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={recordId}
                onChange={(event) => setRecordId(event.target.value)}
              />
            </label>
          ) : null}
        </div>

        {preview ? (
          <div
            data-restore-preview={preview.restoreId}
            className="flex flex-col gap-2 rounded-control bg-sunken px-3 py-2"
          >
            {/* The scope, in words, next to the numbers it was counted under. */}
            <p data-restore-covers className="text-dense text-ink">
              {t('backup.preview', {
                values: {
                  covers: preview.covers,
                  missing: String(preview.totals.missing),
                  changed: String(preview.totals.changed),
                },
              })}
            </p>
            <p className="text-meta text-ink-meta">{t('backup.preview.note')}</p>
            {/* Before the button, not after: somebody deciding whether to
                restore needs to know what it will leave out. */}
            {preview.notRestorable.length > 0 ? (
              <div data-restore-not-restorable className="text-dense text-warning-text">
                <p>{t('backup.notRestorable')}</p>
                <ul className="list-disc ps-5">
                  {preview.notRestorable.map((entry) => (
                    <li key={entry.table} data-restore-not-restorable-table={entry.table}>
                      {entry.table}: {entry.reason}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                intent="primary"
                size="small"
                data-restore-commit
                onClick={() => commit.mutate(preview.restoreId)}
              >
                {t('backup.commit')}
              </Button>
              <Button
                intent="ghost"
                size="small"
                data-restore-cancel
                onClick={() => {
                  void api.cancelRestore({ path: { restoreId: preview.restoreId } });
                  setPreview(null);
                }}
              >
                {t('backup.cancel')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
